/* Shared modal plumbing for the theme's overlay components (the cart drawer
   and the variant sheet): scroll lock, Escape-to-close, a focus trap for
   aria-modal semantics, inert background content, and focus restore to the
   opener. Loaded (guarded) by every section that loads a dependent script;
   the second, duplicate execution is a no-op.

   Modals can stack — the variant sheet opens on top of the cart drawer for
   in-drawer cross-sell picks — so guards register on a stack and only the
   topmost one handles keydown. Escape therefore closes one layer at a time,
   and a lower trap never fights the top one over Tab focus. */
(function () {
  window.Allvar = window.Allvar || {};
  if (window.Allvar.ModalGuard) return;

  const stack = [];

  class ModalGuard {
    /* host      — the modal element; the focus trap keeps Tab inside it.
       onEscape  — called when Escape is pressed while this modal is topmost.
       exempt    — selector for body children that must stay live (e.g. the
                   cart's screen-reader live regions). */
    constructor(host, { onEscape, exempt = '' } = {}) {
      this.host = host;
      this.onEscape = onEscape;
      this.exempt = exempt;
      this.inertedElements = [];
      this.onKeydown = this.onKeydown.bind(this);
    }

    open() {
      if (this.active) return;
      this.active = true;
      stack.push(this);
      this.lastFocus = document.activeElement;
      // Lock scroll behind the modal. iOS Safari ignores overflow on <body>
      // alone in places, so lock the root element too.
      this.prevBodyOverflow = document.body.style.overflow;
      this.prevRootOverflow = document.documentElement.style.overflow;
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      this.setBackgroundInert(true);
      document.addEventListener('keydown', this.onKeydown);
    }

    close({ restoreFocus = true } = {}) {
      if (!this.active) return;
      this.active = false;
      const index = stack.indexOf(this);
      if (index !== -1) stack.splice(index, 1);
      document.body.style.overflow = this.prevBodyOverflow || '';
      document.documentElement.style.overflow = this.prevRootOverflow || '';
      this.setBackgroundInert(false);
      document.removeEventListener('keydown', this.onKeydown);
      if (restoreFocus && this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
      this.lastFocus = null;
    }

    // Everything outside the modal's own section becomes inert, so screen
    // readers and Tab can't wander into content the overlay visually blocks.
    // Restores each element's previous state on close, so stacked modals
    // (sheet over drawer) unwind correctly.
    setBackgroundInert(inert) {
      const modalRoot = this.host.closest('.shopify-section') || this.host;
      const exemptElements = this.exempt
        ? Array.from(document.querySelectorAll(this.exempt))
        : [];

      if (inert) {
        this.inertedElements = Array.from(document.body.children)
          .filter(
            (element) =>
              element !== modalRoot &&
              !exemptElements.includes(element) &&
              !element.contains(modalRoot)
          )
          .map((element) => ({ element, wasInert: element.inert }));
        this.inertedElements.forEach(({ element }) => {
          element.inert = true;
        });
      } else {
        this.inertedElements.forEach(({ element, wasInert }) => {
          element.inert = wasInert;
        });
        this.inertedElements = [];
      }
    }

    onKeydown(event) {
      if (stack[stack.length - 1] !== this) return; // only the top layer reacts
      if (event.key === 'Escape') {
        if (this.onEscape) this.onEscape();
        return;
      }
      // Focus trap: the modal is aria-modal, so Tab must stay within it.
      if (event.key !== 'Tab') return;
      const focusable = Array.from(
        this.host.querySelectorAll(
          'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (!this.host.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && (active === first || !focusable.includes(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  window.Allvar.ModalGuard = ModalGuard;
})();
